import { APP_PAGES, getAppPageHref, type AppPageDestination } from "./app-pages";

export type UpdateKind = "feature" | "improvement" | "fix";
export type UpdateStatus = "live" | "in-review" | "planned";

export interface FeatureUpdate {
	id: string;
	title: string;
	summary: string;
	kind: UpdateKind;
	status: UpdateStatus;
	area:
		| "Sales"
		| "Marketing"
		| "Logistics"
		| "Warehouse"
		| "Inventory"
		| "Accounting";
	audience: string;
	benefit: string;
	steps: readonly string[];
	/** Page links appear beside steps that move to a different app or section. */
	stepDestinations?: Readonly<Record<number, AppPageDestination>>;
	note: string;
	walkthrough?: "crm" | "credit-key";
	/** Source-history evidence; never a claim of current production availability. */
	source?: { date: string; commit: string };
	/** Required for live entries. Set only after the production release is verified. */
	release?: { date: string; commit: string; verifiedAt?: string };
	/** For live entries only; the UI also checks the viewer's route permissions. */
	destination: { path: string; label: string };
}

export const KIND_LABELS: Record<UpdateKind, string> = {
	feature: "New feature",
	improvement: "Improvement",
	fix: "Notable fix",
};

export const STATUS_LABELS: Record<UpdateStatus, string> = {
	live: "Live",
	"in-review": "In review",
	planned: "Coming next",
};

// Verified read-only against Railway production deployments, deployed Convex
// function metadata, production health, and the relevant Credit Key gates.
// See docs/features/cherry-changelog-release-evidence.md for scope and limits.
// These dates identify verified deployments, not invented first-release dates.
const VERIFIED_CHERRY_RELEASE = {
	date: "2026-10-29",
	commit: "d1a91aefcd7b339c878702d74d5cc340adbb86fc",
	verifiedAt: "2026-11-02",
};
const VERIFIED_CUSTOMER_RELEASE = {
	date: "2026-10-30",
	commit: "21e3519e33044bfc4a6449479fed4a83075e3d5a",
	verifiedAt: "2026-11-02",
};

// Curated editorial content, not a generated commit feed.
export const FEATURE_UPDATES: readonly FeatureUpdate[] = [
	{
		id: "credit-key-customer-guide",
		title: "Credit Key, from first question to funded order.",
		summary:
			"A practical guide for sales: help an enabled customer find Credit Key, complete setup, and understand the next step without confusing access with financing approval.",
		kind: "feature",
		status: "live",
		release: VERIFIED_CUSTOMER_RELEASE,
		area: "Sales",
		audience: "Sales team and administrators",
		benefit:
			"Know what to tell the customer, who can enable access, and what to check before the warehouse handoff.",
		stepDestinations: { 0: APP_PAGES.adminUsers, 1: APP_PAGES.customerPayment, 2: APP_PAGES.customerOrders, 3: APP_PAGES.storageOrders },
		steps: [
			"If the customer cannot see Credit Key, ask an administrator to check Admin → Users → Credit Key access. Sales staff do not grant financing access themselves.",
			"The customer signs in to the storefront, opens Dashboard → Payment Methods and finds the Credit Key card, and selects Set up Credit Key or Resume Credit Key setup. Credit Key handles the application and approval.",
			"At checkout, an eligible customer selects Credit Key and completes the offered financing flow. Review the saved order’s verified financing and fulfillment state before promising the next action.",
			"For stored coffee, explain that a warehouse holding purchase and a later release order are separate. Follow the warehouse guide before any bags are shipped.",
		],
		note: "Access is controlled per account and can be paused. Enabling access or completing setup does not approve a credit line, guarantee financing, or confirm payment. Staff should use the current account/order status; never make a second purchase to resolve an uncertain payment.",
		walkthrough: "credit-key",
		source: {
			date: "2026-09-23",
			commit: "aef43b1a9744469d06da631ed5923f7db07a6e69",
		},
		destination: {
			path: "/admin?tab=users",
			label: "Review account access",
		},
	},
	{
		id: "customer-owned-storage",
		title: "A clear home for customer-owned coffee.",
		summary:
			"The warehouse workflow brings the holding DO, pallet locations, customer bag tags, and storage confirmation together before a later release.",
		kind: "feature",
		status: "live",
		release: VERIFIED_CHERRY_RELEASE,
		area: "Warehouse",
		audience: "Warehouse, logistics, and sales teams",
		benefit:
			"Separate coffee held for a customer from sellable inventory, and know exactly when a bag can be picked.",
		stepDestinations: { 0: APP_PAGES.storageOrders },
		steps: [
			"Open Logistics → Orders → Customer storage or the holding DO. Check the purchase, funding, physical arrival, and bag quantities. Do not ship against a holding DO.",
			"Move arrived bags to customer storage and use Assign locations to record the pallet location and quantity for every coffee.",
			"Use Print bag tags. Print at 100% on Letter paper with browser headers and footers off, cut the four tags per sheet, and attach the matching customer/order tag to every bag. Printing alone does not confirm attachment.",
			"Confirm every bag is tagged, save locations, and select Mark stored. Keep bags in storage until there is a separate release DO, then pick from the saved customer locations.",
		],
		note: "Arriving coffee, missing tags or locations, and payment-review blockers must be resolved before release. A Stored for Customer label confirms physical warehouse work; it does not change the purchase into an instruction to ship.",
		source: {
			date: "2026-10-09",
			commit: "250926d3af8a7851f5a53a7ce50fb49e065cb0aa",
		},
		destination: {
			path: "/logistics/orders",
			label: "Open customer storage",
		},
	},
	{
		id: "client-purchase-reports",
		title: "Bring a customer’s buying history to the conversation.",
		summary:
			"Create a client purchase report with pounds and weighted average prices by origin and product, using the selected reporting period.",
		kind: "feature",
		status: "live",
		release: VERIFIED_CHERRY_RELEASE,
		area: "Sales",
		audience: "Sales staff with analytics access",
		benefit:
			"Prepare for a customer conversation with a readable purchase summary and an explicit snapshot time.",
		stepDestinations: { 0: APP_PAGES.salesAnalytics },
		steps: [
			"Open Sales → Analytics and select a client and the reporting period.",
			"Choose Completed Orders, then select Client Report.",
			"Review the client, dates, scope, generated timestamp, and purchase lines before using the report’s export controls.",
		],
		note: "Average prices are weighted by pounds at the recorded item price, before order-level discounts, freight, and tax. This is a purchase snapshot, not an account balance or a quote for a future order.",
		source: {
			date: "2026-10-01",
			commit: "534d9d014a5f4700ae78763d7a93acd08187aa16",
		},
		destination: {
			path: "/sales/analytics",
			label: "Open sales analytics",
		},
	},
	{
		id: "low-stock-locations",
		title: "Know where the last bags are.",
		summary:
			"Daily low-stock reports include warehouse and pallet-location context for in-spot lots, helping the team find the coffee behind an availability alert.",
		kind: "improvement",
		status: "live",
		release: VERIFIED_CHERRY_RELEASE,
		area: "Inventory",
		audience: "Inventory, warehouse, and sales teams",
		benefit:
			"Connect an availability conversation to the physical coffee that needs checking.",
		stepDestinations: { 1: APP_PAGES.inventory },
		steps: [
			"Review the daily low-stock report for the coffee and lot in question.",
			"Read the warehouse and location context, then verify current inventory in Cherry before making a customer commitment.",
			"Coordinate with inventory or warehouse staff if physical counts and recorded availability need review.",
		],
		note: "A low-stock report is a snapshot. It is not a stock reservation, and transit/origin coffee should not be treated as ready-to-pick inventory.",
		source: {
			date: "2026-10-21",
			commit: "14e72e3b730d1d78c2f0da4f76e690fb2bfbd329",
		},
		destination: {
			path: "/inventory/cargos",
			label: "Review inventory lots",
		},
	},
	{
		id: "bill-document-tools",
		title: "Review invoice paperwork with less back-and-forth.",
		summary:
			"Bill review adds document tools for inspecting PDFs, splitting combined invoices, and reviewing converted spreadsheet attachments.",
		kind: "feature",
		status: "live",
		release: VERIFIED_CHERRY_RELEASE,
		area: "Accounting",
		audience: "Authorized bill reviewers and approvers",
		benefit:
			"Keep the source document and bill details together while reviewing the right invoice.",
		stepDestinations: { 0: APP_PAGES.bills },
		steps: [
			"Open the bill in Accounting → Bills and inspect its source document alongside the recorded details.",
			"Use the PDF magnifier for small print. If an attachment contains multiple invoices, use the available split workflow and review each resulting document.",
			"For spreadsheet intake, inspect the PDF conversion before approving. Verify the vendor, invoice number, amounts, and approval assignment against the original document.",
		],
		note: "A document conversion or split does not establish that a bill is correct, approved, exported to accounting, or paid. Existing reviewer and approver permissions still apply.",
		source: {
			date: "2026-08-18",
			commit: "924226291abb2adb46c4d7e08fb55910879ecebb",
		},
		destination: {
			path: "/admin/accounting/bills",
			label: "Open bill review",
		},
	},
	{
		id: "cargo-delay-notices",
		title: "Keep preorder conversations aligned with arrival changes.",
		summary:
			"Cargo ETA changes can notify affected preorder customers, giving the team a clearer starting point for follow-up when incoming coffee is delayed.",
		kind: "improvement",
		status: "live",
		release: VERIFIED_CHERRY_RELEASE,
		area: "Inventory",
		audience: "Inventory and sales teams",
		benefit:
			"Use the current cargo ETA and notification history when explaining a delay to a customer.",
		stepDestinations: { 0: APP_PAGES.inventory },
		steps: [
			"Review the incoming cargo’s current ETA and affected preorder context.",
			"Use the cargo update workflow for the change and check the related notification history.",
			"Before following up, confirm the latest expected arrival and explain the impact on the customer’s order.",
		],
		note: "An ETA is an estimate. A sent or provider-accepted notification does not prove inbox delivery, and a revised ETA is not confirmation that the coffee has arrived.",
		source: {
			date: "2026-08-10",
			commit: "ba13ae4c590fa7362632799eb1990feea548edba",
		},
		destination: {
			path: "/inventory/cargos",
			label: "Review cargo arrivals",
		},
	},
	{
		id: "crm-next-action",
		title: "Every conversation has a next step.",
		summary:
			"Keep lead details, customer conversations, and follow-ups together in the CRM, so the next person knows where to pick up.",
		kind: "feature",
		status: "live",
		release: VERIFIED_CHERRY_RELEASE,
		area: "Sales",
		audience: "Sales team",
		benefit:
			"Less piecing together context. A clearer next action for every lead.",
		stepDestinations: { 0: APP_PAGES.crm },
		steps: [
			"Open Sales → CRM → Opportunities and select a lead.",
			"Review the lead’s owner, status, and activity timeline.",
			"Under Add activity, choose Touch and Outcome, set Next follow-up, write Conversation notes, then select Log. Check Activity timeline for the saved entry.",
		],
		note: "Log saves the activity and next follow-up. Save changes updates the lead’s details. Neither action sends a message to the customer.",
		walkthrough: "crm",
		destination: { path: "/sales/crm/opportunities", label: "Open CRM" },
	},
	{
		id: "contract-savings-drafts",
		source: {
			date: "2026-10-28",
			commit: "81cb881c48ebaaaa5f797cd038d8204fb014da91",
		},
		title: "Build a more informed coffee proposal.",
		summary:
			"Prepare contract-savings campaign drafts using purchase history and proposed coffees, then review the comparison before outreach.",
		kind: "feature",
		status: "live",
		release: VERIFIED_CHERRY_RELEASE,
		area: "Marketing",
		audience: "Sales and authorized campaign managers",
		benefit:
			"Start the conversation with a considered comparison rather than a spreadsheet assembled from scratch.",
		stepDestinations: { 0: APP_PAGES.campaigns, 1: { ...APP_PAGES.salesAnalytics, hint: "Select a client, then choose Compare for campaign under Sales by Origin." } },
		steps: [
			"Open Marketing → Campaigns and create a draft with the purchase window you want to review.",
			"Use Sales → Analytics → Compare for campaign to select historical coffee lots and proposed coffees.",
			"Review the estimates and comparison. Authorized campaign managers can add the client to the draft.",
		],
		note: "Draft preparation only: this workflow does not send emails, publish response links, or notify sales. Estimates and final commercial terms need review.",
		destination: { path: "/marketing/campaigns", label: "Open campaigns" },
	},
	{
		id: "logistics-review-alerts",
		source: {
			date: "2026-10-06",
			commit: "823a86b39ddc853fc674c17cc85730a847caebf0",
		},
		title: "See what changed before the next handoff.",
		summary:
			"Review alerts highlight updates from teammates across inbound deliveries, international purchases, and outbound shipments.",
		kind: "improvement",
		status: "live",
		release: VERIFIED_CHERRY_RELEASE,
		area: "Logistics",
		audience: "Staff with logistics editing access",
		benefit:
			"Make handoffs with the latest warehouse and shipment context in view.",
		stepDestinations: { 1: APP_PAGES.inbound },
		steps: [
			"Look for a red Review label beside a logistics section in the sidebar.",
			"Open that section and inspect the updated entries.",
			"Select Mark reviewed under the active sidebar option after checking the changes.",
		],
		note: "Review state belongs to your login and section. Newer updates stay pending, and marking reviewed does not change shipment status.",
		destination: {
			path: "/logistics/inbound-deliveries",
			label: "Open inbound deliveries",
		},
	},
];

export function validateUpdates(entries: readonly FeatureUpdate[]): void {
	const ids = new Set<string>();
	for (const entry of entries) {
		if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(entry.id) || ids.has(entry.id)) {
			throw new Error(
				`Changelog IDs must be unique URL-safe slugs: ${entry.id}`,
			);
		}
		ids.add(entry.id);
		if (entry.status === "live" && !entry.release) {
			throw new Error(
				`Live update requires verified release evidence: ${entry.id}`,
			);
		}
		for (const evidence of [entry.release, entry.source]) {
			if (!evidence) continue;
			const { date, commit } = evidence;
			if (
				!/^\d{4}-\d{2}-\d{2}$/.test(date) ||
				!Number.isFinite(Date.parse(`${date}T00:00:00Z`)) ||
				new Date(`${date}T00:00:00Z`).toISOString().slice(0, 10) !== date ||
				!/^[a-f0-9]{40}$/.test(commit)
			) {
				throw new Error(`Invalid release date or full commit SHA: ${entry.id}`);
			}
		}
		if (entry.release?.verifiedAt) {
			const date = entry.release.verifiedAt;
			if (
				!/^\d{4}-\d{2}-\d{2}$/.test(date) ||
				!Number.isFinite(Date.parse(`${date}T00:00:00Z`)) ||
				new Date(`${date}T00:00:00Z`).toISOString().slice(0, 10) !== date ||
				date < entry.release.date
			) throw new Error(`Invalid release verification date: ${entry.id}`);
		}
		if (
			!entry.title.trim() ||
			!entry.summary.trim() ||
			!entry.benefit.trim() ||
			!entry.audience.trim() ||
			!entry.note.trim() ||
			entry.steps.length === 0 ||
			entry.steps.some((step) => !step.trim()) ||
			!/^\/(?!\/)/.test(entry.destination.path) ||
			!entry.destination.label.trim()
		) {
			throw new Error(`Incomplete changelog entry: ${entry.id}`);
		}
		if (entry.stepDestinations) {
			if (Object.keys(entry.stepDestinations).length === 0) throw new Error(`Missing step destinations: ${entry.id}`);
			for (const [index, destination] of Object.entries(entry.stepDestinations)) {
				if (!/^\d+$/.test(index) || Number(index) >= entry.steps.length || !destination.label.trim()) {
					throw new Error(`Invalid step destination: ${entry.id}`);
				}
				getAppPageHref(destination);
			}
		}
	}
}

validateUpdates(FEATURE_UPDATES);

export function filterUpdates(
	entries: readonly FeatureUpdate[],
	{
		status,
		query,
		includeFixes,
	}: { status: UpdateStatus | "all"; query: string; includeFixes: boolean },
): FeatureUpdate[] {
	const search = query.trim().toLocaleLowerCase();
	return entries.filter(
		(entry) =>
			(includeFixes || entry.kind !== "fix") &&
			(status === "all" || entry.status === status) &&
			[
				entry.title,
				entry.summary,
				entry.area,
				entry.audience,
				entry.benefit,
				entry.note,
				...entry.steps,
			]
				.join(" ")
				.toLocaleLowerCase()
				.includes(search),
	);
}
